import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, Eye, EyeOff, Loader2, Pencil, RotateCcw, X } from 'lucide-react';
import type {
  ReportDefinition,
  ReportPrintItemOption,
  ReportPrintOptions,
  ReportResult,
} from '../../../../types/reports';
import {
  DEFAULT_REPORT_PRINT_OPTIONS,
  breakdownRowKey,
  buildPrintOptions,
} from '../../../../utils/reportPrintOptions';

interface PrintOptionsModalProps {
  open: boolean;
  report: ReportDefinition;
  /** The generated result; `undefined` before the report has been run. */
  result?: ReportResult;
  /** The layout saved on the server (defaults while it loads). */
  saved: ReportPrintOptions;
  /** True while the saved layout is being fetched. */
  loading: boolean;
  saving: boolean;
  onSave: (options: ReportPrintOptions) => void;
  onClose: () => void;
}

/**
 * Configures exactly what a report's PDF / print copy contains.
 *
 * The layout lives on the server per report, so every user prints the same
 * document. Each summary card and breakdown block can be switched off and given
 * a custom printed label; the original label is the match key, so leaving the
 * "print as" field empty keeps the canonical wording.
 */
export function PrintOptionsModal({
  open,
  report,
  result,
  saved,
  loading,
  saving,
  onSave,
  onClose,
}: PrintOptionsModalProps) {
  // The saved layout plus the items this particular result emits.
  const initial = useMemo(() => buildPrintOptions(saved, result), [saved, result]);
  const [draft, setDraft] = useState<ReportPrintOptions>(initial);
  /**
   * Seed the draft on the first open (after the saved layout has loaded) and
   * again when a new report result arrives — e.g. the modal auto-generates the
   * report to learn its cards/rows. A background layout refetch must never
   * overwrite edits the user is typing.
   */
  const seeded = useRef(false);
  const seededResult = useRef<ReportResult | undefined>(undefined);
  useEffect(() => {
    if (!open) {
      seeded.current = false;
      seededResult.current = undefined;
      return;
    }
    if (loading) return;
    if (seeded.current && seededResult.current === result) return;
    seeded.current = true;
    seededResult.current = result;
    setDraft(initial);
    // `initial` is intentionally read at seed time only — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, result]);

  /** The blocks/rows the current result emits, in report order. */
  const breakdownBlocks = useMemo(
    () =>
      (result?.breakdowns ?? []).map((breakdown) => ({
        title: breakdown.title,
        rows: breakdown.items.map((item) => item.label),
      })),
    [result],
  );

  if (!open) return null;

  const detailsForcedOff = report.printWithoutDetails === true;
  const nothingSelected = !draft.summary && !draft.breakdowns && !(draft.details && !detailsForcedOff);
  const hasItems = Boolean(result);

  const setItem = (
    section: 'summaryItems' | 'breakdownItems' | 'breakdownItemOptions',
    key: string,
    patch: Partial<ReportPrintItemOption>,
  ) => {
    setDraft((current) => ({
      ...current,
      [section]: current[section].map((item) =>
        item.key === key ? { ...item, ...patch } : item,
      ),
    }));
  };

  const setAllBreakdownRows = (breakdownTitle: string, visible: boolean) => {
    setDraft((current) => ({
      ...current,
      breakdownItemOptions: current.breakdownItemOptions.map((item) =>
        item.key.startsWith(`${breakdownTitle}::`) ? { ...item, visible } : item,
      ),
    }));
  };

  const reset = () => {
    setDraft(
      buildPrintOptions(
        { ...DEFAULT_REPORT_PRINT_OPTIONS, details: !detailsForcedOff },
        result,
      ),
    );
  };

  const save = () => {
    /**
     * Only send what differs from the default. The API treats a missing item as
     * "print it with its original label", so this keeps the stored setting (and
     * the request) small even for reports with hundreds of breakdown rows.
     */
    const toOverrides = (items: ReportPrintItemOption[]) =>
      items
        .filter((item) => !item.visible || Boolean(item.label?.trim()))
        .map((item) => ({
          key: item.key,
          ...(item.label?.trim() ? { label: item.label.trim() } : {}),
          visible: item.visible,
        }));

    onSave({
      ...draft,
      details: detailsForcedOff ? false : draft.details,
      summaryItems: toOverrides(draft.summaryItems),
      breakdownItems: toOverrides(draft.breakdownItems),
      breakdownItemOptions: toOverrides(draft.breakdownItemOptions),
    });
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Print options"
    >
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-text">Print options</h2>
            <p className="mt-0.5 truncate text-xs text-text-muted">
              {report.name} · choose what the PDF / printed copy contains
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-text-muted">
              <Loader2 size={16} className="animate-spin text-primary" />
              Preparing print options…
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <BlockToggle
                  checked={draft.summary}
                  onChange={(value) => setDraft((current) => ({ ...current, summary: value }))}
                  title="Summary"
                  description="The KPI cards at the top of the document"
                />
                <BlockToggle
                  checked={draft.breakdowns}
                  onChange={(value) => setDraft((current) => ({ ...current, breakdowns: value }))}
                  title="Breakdown"
                  description="The analysis blocks below the summary"
                />
                <BlockToggle
                  checked={draft.details}
                  disabled={detailsForcedOff}
                  onChange={(value) => setDraft((current) => ({ ...current, details: value }))}
                  title="Details"
                  description={
                    detailsForcedOff
                      ? 'This report never prints its detail table'
                      : 'The record-by-record ledger with page totals'
                  }
                />
              </div>

              {!hasItems && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-xs text-text-muted">
                  <AlertCircle size={14} className="mt-0.5 shrink-0 text-primary" />
                  <span>
                    Generate the report once to list its summary cards and breakdown blocks,
                    then choose and rename them here.
                  </span>
                </div>
              )}

              {hasItems && (
                <>
                  <ItemSection
                    title="Summary cards"
                    hint="Untick to hide · click a label and type to rename it (empty = original)"
                    items={draft.summaryItems}
                    disabled={!draft.summary}
                    onChange={(key, patch) => setItem('summaryItems', key, patch)}
                    onChangeAll={(visible) =>
                      setDraft((current) => ({
                        ...current,
                        summaryItems: current.summaryItems.map((item) => ({ ...item, visible })),
                      }))
                    }
                  />
                  <BreakdownOptionsSection
                    breakdowns={breakdownBlocks}
                    blockOptions={draft.breakdownItems}
                    rowOptions={draft.breakdownItemOptions}
                    disabled={!draft.breakdowns}
                    onBlockChange={(key, patch) => setItem('breakdownItems', key, patch)}
                    onBlockAll={(visible) =>
                      setDraft((current) => ({
                        ...current,
                        breakdownItems: current.breakdownItems.map((item) => ({
                          ...item,
                          visible,
                        })),
                      }))
                    }
                    onRowChange={(key, patch) => setItem('breakdownItemOptions', key, patch)}
                    onRowAll={setAllBreakdownRows}
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border bg-background/40 px-5 py-3">
          <button
            type="button"
            onClick={reset}
            disabled={loading}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-text-muted transition-colors hover:text-text disabled:opacity-50"
          >
            <RotateCcw size={14} />
            Reset to defaults
          </button>
          {nothingSelected && (
            <span className="text-[0.6875rem] font-medium text-danger">
              Select at least one section
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-lg px-3 text-sm font-medium text-text-muted transition-colors hover:text-text"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3.5 text-sm font-semibold text-white shadow-sm shadow-primary/25 transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BlockToggle({
  checked,
  disabled,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  title: string;
  description: string;
}) {
  return (
    <label
      className={`flex items-center gap-2.5 rounded-xl border border-border px-3 py-2 ${
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary/40'
      }`}
    >
      <input
        type="checkbox"
        checked={checked && !disabled}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-primary disabled:opacity-50"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-text">{title}</span>
        <span className="block text-[0.6875rem] text-text-muted">{description}</span>
      </span>
    </label>
  );
}

function ItemSection({
  title,
  hint,
  items,
  disabled,
  onChange,
  onChangeAll,
}: {
  title: string;
  hint: string;
  items: ReportPrintItemOption[];
  disabled: boolean;
  onChange: (key: string, patch: Partial<ReportPrintItemOption>) => void;
  /** Show/hide every box in this section at once. */
  onChangeAll: (visible: boolean) => void;
}) {
  const allVisible = items.length > 0 && items.every((item) => item.visible);
  const someVisible = items.some((item) => item.visible);

  return (
    <div className={`mt-5 ${disabled ? 'opacity-50' : ''}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wide text-text-muted">
          {title}
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-[0.625rem] text-text-muted">
            {items.filter((item) => item.visible).length} of {items.length} printed
          </span>
          {/* One checkbox for the whole section: hide/show every box at once. */}
          <AllToggle
            checked={allVisible}
            indeterminate={someVisible && !allVisible}
            disabled={disabled || items.length === 0}
            title={allVisible ? `Hide all ${title.toLowerCase()}` : `Show all ${title.toLowerCase()}`}
            onToggle={() => onChangeAll(!allVisible)}
          />
        </div>
      </div>
      <p className="mb-2 text-[0.6875rem] text-text-muted">{hint}</p>
      <div className="space-y-1">
        {items.map((item) => (
          <ItemRow
            key={item.key}
            item={item}
            disabled={disabled}
            onChange={(patch) => onChange(item.key, patch)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Breakdown blocks with their rows.
 *
 * The block checkbox removes the whole block; each row below it can be removed
 * on its own, and a block whose rows are all unchecked disappears from the
 * document entirely (the API applies the same rule).
 */
function BreakdownOptionsSection({
  breakdowns,
  blockOptions,
  rowOptions,
  disabled,
  onBlockChange,
  onBlockAll,
  onRowChange,
  onRowAll,
}: {
  breakdowns: Array<{ title: string; rows: string[] }>;
  blockOptions: ReportPrintItemOption[];
  rowOptions: ReportPrintItemOption[];
  disabled: boolean;
  onBlockChange: (key: string, patch: Partial<ReportPrintItemOption>) => void;
  onBlockAll: (visible: boolean) => void;
  onRowChange: (key: string, patch: Partial<ReportPrintItemOption>) => void;
  onRowAll: (breakdownTitle: string, visible: boolean) => void;
}) {
  const blocksByKey = new Map(blockOptions.map((option) => [option.key, option]));
  const rowsByKey = new Map(rowOptions.map((option) => [option.key, option]));

  const blocks = breakdowns.map((breakdown) => ({
    ...breakdown,
    option:
      blocksByKey.get(breakdown.title) ??
      ({ key: breakdown.title, label: '', visible: true } as ReportPrintItemOption),
    rows: breakdown.rows.map((label) => {
      const key = breakdownRowKey(breakdown.title, label);
      return {
        key,
        label,
        option:
          rowsByKey.get(key) ??
          ({ key, label: '', visible: true } as ReportPrintItemOption),
      };
    }),
  }));

  const allVisible = blocks.length > 0 && blocks.every((block) => block.option.visible);
  const someVisible = blocks.some((block) => block.option.visible);

  return (
    <div className={`mt-5 ${disabled ? 'opacity-50' : ''}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wide text-text-muted">
          Breakdown blocks
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-[0.625rem] text-text-muted">
            {blocks.filter((block) => block.option.visible).length} of {blocks.length} printed
          </span>
          <AllToggle
            checked={allVisible}
            indeterminate={someVisible && !allVisible}
            disabled={disabled || blocks.length === 0}
            title={allVisible ? 'Hide all breakdown blocks' : 'Show all breakdown blocks'}
            onToggle={() => onBlockAll(!allVisible)}
          />
        </div>
      </div>
      <p className="mb-2 text-[0.6875rem] text-text-muted">
        Untick a block to hide it · each row below can be hidden on its own · click a label and
        type to rename it (empty = original)
      </p>

      {blocks.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-background/40 px-3 py-3 text-xs text-text-muted">
          No breakdown rows for this period — the report returned no data. Rows appear here once
          the report has data, and can then be shown/hidden individually.
        </div>
      ) : (
        <div className="space-y-2">
          {blocks.map((block) => {
          const rowItems = block.rows.map((row) => row.option);
          const rowsAllVisible = rowItems.length > 0 && rowItems.every((item) => item.visible);
          const rowsSomeVisible = rowItems.some((item) => item.visible);
          const rowsDisabled = disabled || !block.option.visible;

          return (
            <div
              key={block.title}
              className={`overflow-hidden rounded-xl border ${
                block.option.visible ? 'border-border' : 'border-border/60 bg-background/20'
              }`}
            >
              {/* Block header */}
              <div className="flex items-center gap-2 border-b border-border/60 bg-background/40 px-2.5 py-2">
                <input
                  type="checkbox"
                  checked={block.option.visible}
                  disabled={disabled}
                  onChange={(event) =>
                    onBlockChange(block.title, { visible: event.target.checked })
                  }
                  className="h-4 w-4 shrink-0 accent-primary disabled:opacity-50"
                  aria-label={`Print ${block.title}`}
                />
                {block.option.visible ? (
                  <Eye size={13} className="shrink-0 text-text-muted" />
                ) : (
                  <EyeOff size={13} className="shrink-0 text-text-muted" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      value={block.option.label ?? ''}
                      disabled={disabled}
                      placeholder={block.title}
                      maxLength={200}
                      onChange={(event) =>
                        onBlockChange(block.title, { label: event.target.value })
                      }
                      className="w-full rounded-md border border-transparent bg-transparent py-0.5 pl-1.5 pr-6 text-sm font-medium text-text transition-colors placeholder:text-text-muted hover:border-border focus:border-border focus:bg-surface focus:outline-none focus:ring-1 focus:ring-primary/40 disabled:cursor-not-allowed"
                      title="Click and type to rename this printed block title (empty = original)"
                    />
                    <Pencil
                      size={11}
                      className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-text-muted/60"
                    />
                  </div>
                  {block.option.label ? (
                    <span className="block px-1.5 text-[0.625rem] text-text-muted">
                      original: {block.title}
                    </span>
                  ) : null}
                </div>
                {block.option.label ? (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onBlockChange(block.title, { label: '' })}
                    className="shrink-0 rounded p-1 text-text-muted transition-colors hover:text-danger disabled:opacity-50"
                    title="Use the original title"
                  >
                    <RotateCcw size={12} />
                  </button>
                ) : null}
                {block.rows.length > 0 ? (
                  <AllToggle
                    small
                    checked={rowsAllVisible}
                    indeterminate={rowsSomeVisible && !rowsAllVisible}
                    disabled={rowsDisabled}
                    title={
                      rowsAllVisible
                        ? `Hide all rows in ${block.title}`
                        : `Show all rows in ${block.title}`
                    }
                    onToggle={() => onRowAll(block.title, !rowsAllVisible)}
                  />
                ) : null}
              </div>

              {/* Rows */}
              {block.rows.length > 0 ? (
                <div className={`space-y-1 px-2.5 py-2 ${rowsDisabled ? 'opacity-50' : ''}`}>
                  {block.rows.map((row) => (
                    <ItemRow
                      key={row.key}
                      item={row.option}
                      placeholder={row.label}
                      disabled={rowsDisabled}
                      onChange={(patch) => onRowChange(row.key, patch)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}

/** A summary card / breakdown row: visibility checkbox + optional printed label. */
function ItemRow({
  item,
  placeholder,
  disabled,
  onChange,
}: {
  item: ReportPrintItemOption;
  /** Text shown when no custom label is set (defaults to the item's key). */
  placeholder?: string;
  disabled: boolean;
  onChange: (patch: Partial<ReportPrintItemOption>) => void;
}) {
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-colors ${
        item.visible ? 'border-border bg-background/40' : 'border-border/60 bg-background/20'
      }`}
    >
      <input
        type="checkbox"
        checked={item.visible}
        disabled={disabled}
        onChange={(event) => onChange({ visible: event.target.checked })}
        className="h-4 w-4 shrink-0 accent-primary disabled:opacity-50"
        aria-label={`Print ${placeholder ?? item.key}`}
      />
      {item.visible ? (
        <Eye size={13} className="shrink-0 text-text-muted" />
      ) : (
        <EyeOff size={13} className="shrink-0 text-text-muted" />
      )}
      <div className="min-w-0 flex-1">
        <div className="relative">
          <input
            type="text"
            value={item.label ?? ''}
            disabled={disabled}
            placeholder={placeholder ?? item.key}
            maxLength={200}
            onChange={(event) => onChange({ label: event.target.value })}
            className="w-full rounded-md border border-transparent bg-transparent py-0.5 pl-1.5 pr-6 text-sm text-text transition-colors placeholder:text-text-muted hover:border-border focus:border-border focus:bg-surface focus:outline-none focus:ring-1 focus:ring-primary/40 disabled:cursor-not-allowed"
            title="Click and type to rename this printed label (empty = original)"
          />
          {/* The label is an editable field; the pencil makes that discoverable. */}
          <Pencil
            size={11}
            className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-text-muted/60"
          />
        </div>
        {item.label ? (
          <span className="block px-1.5 text-[0.625rem] text-text-muted">
            original: {placeholder ?? item.key}
          </span>
        ) : null}
      </div>
      {item.label ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ label: '' })}
          className="shrink-0 rounded p-1 text-text-muted transition-colors hover:text-danger disabled:opacity-50"
          title="Use the original label"
        >
          <RotateCcw size={12} />
        </button>
      ) : null}
    </div>
  );
}

/** Compact "All" checkbox; `indeterminate` shows a mixed selection. */
function AllToggle({
  checked,
  indeterminate,
  disabled,
  onToggle,
  title,
  small,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onToggle: () => void;
  title: string;
  small?: boolean;
}) {
  return (
    <label
      className={`inline-flex items-center gap-1.5 rounded-md border border-border bg-surface font-medium text-text-muted ${
        small ? 'px-1.5 py-0.5 text-[0.625rem]' : 'px-2 py-1 text-[0.6875rem]'
      } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary/40 hover:text-text'}`}
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        ref={(element) => {
          // `indeterminate` cannot be set through JSX: it shows the mixed
          // state when only some of the boxes are ticked.
          if (element) element.indeterminate = indeterminate;
        }}
        onChange={onToggle}
        className="h-3.5 w-3.5 accent-primary disabled:opacity-50"
        aria-label={title}
      />
      All
    </label>
  );
}
